/**
 * 路由守衛。前端是「體驗層」，後端仍是權威（403 為最終防線）。
 */

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result } from 'antd';
import type { Role } from '@bom/shared';
import { useSessionStore } from '../stores/sessionStore';
import { isExpired } from '../lib/jwt';

/** 需登入（有效 session token）。首登强制改密：must_change_password 时一律先去改密页。 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { sessionJwt, user } = useSessionStore();
  const location = useLocation();
  if (isExpired(sessionJwt)) return <Navigate to="/login" replace />;
  if (user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children}</>;
}

/** 需可打業務 API：platform_admin 用 session token；一般 user 需 tenant token */
export function RequireBusinessAccess({ children }: { children: ReactNode }) {
  const { sessionJwt, user, tenantJwt } = useSessionStore();
  if (isExpired(sessionJwt)) return <Navigate to="/login" replace />;
  if (user?.must_change_password) return <Navigate to="/change-password" replace />;
  const ok = user?.is_platform_admin ? !isExpired(sessionJwt) : !isExpired(tenantJwt);
  if (!ok) return <Navigate to="/select-tenant" replace />;
  return <>{children}</>;
}

/** 需特定 tenant 角色（platform_admin 視為通過） */
export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, role } = useSessionStore();
  const allowed = user?.is_platform_admin || (role !== null && roles.includes(role));
  if (!allowed) {
    return <Result status="403" title="403" subTitle="您没有权限访问此页面。" />;
  }
  return <>{children}</>;
}

/** 需 platform_admin */
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { user } = useSessionStore();
  if (!user?.is_platform_admin) {
    return <Result status="403" title="403" subTitle="仅平台管理员可访问。" />;
  }
  return <>{children}</>;
}

/** 已登入者不應再看到登入頁 */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { sessionJwt, user } = useSessionStore();
  const location = useLocation();
  if (!isExpired(sessionJwt)) {
    if (user?.must_change_password) return <Navigate to="/change-password" replace />;
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
